import { ComponentType, ReactElement } from 'react';

import { IconProps } from '@components/icons';

import { MenuProps } from './menus/types';

import { FilterActions } from '../table-store';

type FilterConfigSelectValue = {
  value: string;
  label: string;
  icon?: ReactElement<IconProps>;
};

type FilterConfigQuickFilter = {
  id: string;
  icon?: ReactElement<IconProps>;
  label: string;
  apply: Partial<FilterData>;
};

export type SeparatorConfig = {
  id: 'separator';
  type: 'separator';
};

type SelectFilterConfig = {
  id: string;
  icon?: ReactElement<IconProps>;
  label: string;
  type: 'single_select' | 'multi_select' | 'user' | 'issues';
  values:
    | Array<FilterConfigSelectValue>
    | ((filter: Partial<FilterData>) => Array<FilterConfigSelectValue>);
  quick?: Array<FilterConfigQuickFilter>;
  allowedComparers?: Array<FilterComparer>;
  toExternalQuery?: (filter: AppliedFilterData) => Record<string, any>;
};

type BasicFilterConfig = {
  id: string;
  icon?: ReactElement<IconProps>;
  label: string;
  type:
    | 'progress'
    | 'text'
    | 'paragraph'
    | 'email'
    | 'phone_number'
    | 'boolean'
    | 'decimal'
    | 'number'
    | 'json'
    | 'file'
    | 'file_multi';
  allowedComparers?: Array<FilterComparer>;
  toExternalQuery?: (filter: AppliedFilterData) => Record<string, any>;
};

type DateFilterConfig = Omit<BasicFilterConfig, 'type' | 'allowedComparers'> & {
  type: 'date' | 'datetime';
  allowedRelativeComparers?: Array<FilterComparer>;
  allowedAbsoluteComparers?: Array<FilterComparer>;
};

export type CustomAppliedFilterProps = {
  filter: AppliedFilterData;
  config: CustomFilterConfig;
  actions: FilterActions;
};

export type CustomAppliedFilter = ComponentType<CustomAppliedFilterProps>;

export type CustomFilterConfig = {
  id: string;
  icon?: ReactElement<IconProps>;
  label: string;
  type: 'custom';
  customMenu: ComponentType<MenuProps>;
  customAppliedFilter?: CustomAppliedFilter;
  allowedComparers?: Array<FilterComparer>;
  toExternalQuery?: (filter: AppliedFilterData) => Record<string, any>;
};

export type SelectableFilterConfigField =
  | SelectFilterConfig
  | BasicFilterConfig
  | CustomFilterConfig
  | DateFilterConfig;
export type CategoryFilterFields = Array<SelectableFilterConfigField | SeparatorConfig>;

export type CategoryFilterConfig = {
  id: string;
  icon?: ReactElement<IconProps>;
  label: string;
  type: 'category';
  fields: CategoryFilterFields;
  toExternalQuery?: (filter: AppliedFilterData) => Record<string, any>;
};

export type QuickFilterConfig = {
  id: string;
  type: 'quick_filters';
  quick: Array<FilterConfigQuickFilter>;
};

export type FilterConfigType =
  | SelectableFilterConfigField['type']
  | CategoryFilterConfig['type']
  | SeparatorConfig['type']
  | QuickFilterConfig['type'];
export type FilterConfigField =
  | SelectableFilterConfigField
  | CategoryFilterConfig
  | SeparatorConfig
  | QuickFilterConfig;
export type FilterConfig = Array<FilterConfigField>;
type FilterComparerSingle =
  | 'after'
  | 'before'
  | 'between'
  | 'contains'
  | 'eq'
  | 'gt'
  | 'is'
  | 'is_empty'
  | 'is_none'
  | 'is_not_empty'
  | 'is_rated_by'
  | 'lt'
  | 'not_contains'
  | 'not_is'
  | 'not_is_rated_by'
  | 'not_starts'
  | 'on'
  | 'starts'
  | 'today'
  | 'yesterday'
  | 'contains_multi';

type FilterComparerMulti = 'any_of' | 'none_of' | 'contains_multi';

export type FilterComparer = FilterComparerSingle | FilterComparerMulti;

export type FilterValue = string | Array<string>;

export type FilterData = {
  id: string;
  category: string;
  value?: FilterValue;
  field?: string;
  comparer?: FilterComparer;
};

export type AppliedFilterData = {
  id: string;
  category: string;
  value: FilterValue;
  field?: string;
  comparer: FilterComparer;
};

export type FiltersConjunctionType = 'and' | 'or';

export type FilterState = {
  appliedFilters: Array<FilterData>;
  isOpen: boolean;
  conjunction: FiltersConjunctionType;
};

export type FilterStoreState = Pick<FilterState, 'isOpen' | 'conjunction'> & {
  appliedFilters: Array<AppliedFilterData>;
};

export const getFilterColumnId = (f: Pick<FilterData, 'category' | 'field'>): string =>
  f.field ?? f.category;

export const getActiveFilters = <T extends Pick<FilterData, 'category' | 'field'>>(
  filters: Array<T>,
  configIds: Set<string>,
): Array<T> => filters.filter((f) => configIds.has(getFilterColumnId(f)));

export const getFilterConfigIds = (config: FilterConfig): Set<string> => {
  const ids = new Set<string>();
  for (const configItem of config) {
    if (configItem.type === 'category') {
      for (const field of configItem.fields) {
        ids.add(field.id);
      }
    } else if (configItem.type !== 'separator' && configItem.type !== 'quick_filters') {
      ids.add(configItem.id);
    }
  }
  return ids;
};
